import { Ref } from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Label text to display above the input field.
   */
  label?: string;

  /**
   * Marks the input field as required. Displays an indicator if set to true.
   */
  required?: boolean;

  /**
   * Icon element to display inside the input field.
   */
  icon?: React.ReactNode;

  /**
   * The current value of the input field.
   */
  value?: string;

  /**
   * Indicates whether the input value has been changed.
   */
  hasChanged?: boolean;

  /**
   * Custom style variant to apply when the input value changes.
   * Options are defined in the `InputChangeVariant` enum.
   */
  inputChangedStyle?: InputChangeVariant;

  /**
   * Highlights the input visually when the value changes, if set to true.
   */
  highlightOnChange?: boolean;

  /**
   * Determines the position of the icon inside the input field.
   * Possible values are 'left' or 'right'.
   */
  iconPosition?: 'left' | 'right';

  /**
   * Enables or disables the click functionality for the icon.
   */
  iconClickable?: boolean;

  /**
   * The debounce time in milliseconds for delaying input changes.
   */
  debounceTime?: number;

  /**
   * Optional CSS class to apply custom styling.
   */
  className?: string;

  /**
   * Manually show the error border for the input field.
   */
  showErrorBorder?: boolean;

  /**
   * Disables showing error message for the input field.
   */
  hideErrorMessage?: boolean;

  /**
   * Show/hide the eye icon for password fields.
   */
  showEyeIcon?: boolean;

  /**
   * Ref object to access the input element directly.
   */
  ref?: Ref<HTMLInputElement>;

  /**
   * Callback function triggered when the icon is clicked.
   */
  onIconClick?: () => void;

  /**
   * Validation function for the input value. Should return true if valid, false otherwise.
   * @param value - The input value to validate.
   */
  validate?: (value: string) => boolean;

  /**
   * Custom error message to display if validation fails.
   * Can be a string or a function that generates a message based on the input value.
   * @param value - The input value causing the error.
   */
  customErrorMessage?: string | ((value: string) => string);
}

export enum InputChangeVariant {
  Default = '',
  Secondary = 'secondary',
}

export const EmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export enum ErrorMessage {
  FieldRequired = 'Field is required',
  PatternMismatch = 'Pattern mismatch',
  InvalidEmail = 'Invalid email',
  InvalidValue = 'Invalid value',
}
